/**
 * Parser base class and registry for Synesthesia
 * Provides a pluggable architecture for language-specific parsers
 */

/**
 * Base Parser class that all language parsers should extend
 */
export class Parser {
  constructor(options = {}) {
    this.options = options;
  }

  /**
   * Parse text into HTML with syntax highlighting
   * @param {string} text - Source code to parse
   * @returns {string} HTML with syntax highlighting
   */
  parse(text) {
    throw new Error('Parser.parse() must be implemented by subclass');
  }

  /**
   * Get parser metadata
   * @returns {Object} Parser information
   */
  static get metadata() {
    return {
      name: 'base',
      version: '1.0.0',
      languages: [],
      fileExtensions: [],
      mimeTypes: []
    };
  }

  /**
   * Escape HTML special characters
   * @param {string} text - Raw text to escape
   * @returns {string} Escaped HTML-safe text
   */
  static escapeHtml(text) {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    };
    return text.replace(/[&<>"']/g, m => map[m]);
  }

  /**
   * Wrap text in a span with given class
   * @param {string} text - Text to wrap
   * @param {string} className - CSS class name
   * @returns {string} HTML span element
   */
  static span(text, className) {
    return `<span class="${className}">${text}</span>`;
  }
}

/**
 * Parser Registry for managing available parsers
 */
export class ParserRegistry {
  constructor() {
    this.parsers = new Map();
    this.languageMap = new Map();
    this.extensionMap = new Map();
    this.mimeMap = new Map();
  }

  /**
   * Register a parser
   * @param {string} id - Unique parser identifier
   * @param {Parser} parserClass - Parser class (must extend Parser)
   * @param {Object} options - Registration options
   */
  register(id, parserClass, options = {}) {
    if (!(parserClass.prototype instanceof Parser) && parserClass !== Parser) {
      throw new Error('Parser must extend the Parser base class');
    }

    this.parsers.set(id, { class: parserClass, options });
    
    const metadata = parserClass.metadata;
    
    // Map languages to parser
    if (metadata.languages) {
      metadata.languages.forEach(lang => {
        this.languageMap.set(lang.toLowerCase(), id);
      });
    }
    
    // Map file extensions to parser
    if (metadata.fileExtensions) {
      metadata.fileExtensions.forEach(ext => {
        this.extensionMap.set(ext.toLowerCase(), id);
      });
    }
    
    // Map MIME types to parser
    if (metadata.mimeTypes) {
      metadata.mimeTypes.forEach(mime => {
        this.mimeMap.set(mime.toLowerCase(), id);
      });
    }
  }

  /**
   * Unregister a parser
   * @param {string} id - Parser identifier
   */
  unregister(id) {
    const parser = this.parsers.get(id);
    if (!parser) return;

    const metadata = parser.class.metadata;
    
    // Remove language mappings
    metadata.languages?.forEach(lang => {
      if (this.languageMap.get(lang.toLowerCase()) === id) {
        this.languageMap.delete(lang.toLowerCase());
      }
    });
    
    // Remove extension mappings
    metadata.fileExtensions?.forEach(ext => {
      if (this.extensionMap.get(ext.toLowerCase()) === id) {
        this.extensionMap.delete(ext.toLowerCase());
      }
    });
    
    // Remove MIME mappings
    metadata.mimeTypes?.forEach(mime => {
      if (this.mimeMap.get(mime.toLowerCase()) === id) {
        this.mimeMap.delete(mime.toLowerCase());
      }
    });
    
    this.parsers.delete(id);
  }

  /**
   * Get a parser by ID
   * @param {string} id - Parser identifier
   * @returns {Parser|null} Parser instance or null
   */
  get(id) {
    const parser = this.parsers.get(id);
    return parser ? new parser.class(parser.options) : null;
  }

  /**
   * Get a parser by language name
   * @param {string} language - Programming language name
   * @returns {Parser|null} Parser instance or null
   */
  getByLanguage(language) {
    const id = this.languageMap.get(language.toLowerCase());
    return id ? this.get(id) : null;
  }

  /**
   * Get a parser by file extension
   * @param {string} extension - File extension (with or without dot)
   * @returns {Parser|null} Parser instance or null
   */
  getByExtension(extension) {
    const ext = extension.startsWith('.') ? extension.slice(1) : extension;
    const id = this.extensionMap.get(ext.toLowerCase());
    return id ? this.get(id) : null;
  }

  /**
   * Get a parser by MIME type
   * @param {string} mimeType - MIME type
   * @returns {Parser|null} Parser instance or null
   */
  getByMimeType(mimeType) {
    const id = this.mimeMap.get(mimeType.toLowerCase());
    return id ? this.get(id) : null;
  }

  /**
   * List all registered parsers
   * @returns {Array} Array of parser metadata
   */
  list() {
    return Array.from(this.parsers.entries()).map(([id, parser]) => ({
      id,
      ...parser.class.metadata
    }));
  }

  /**
   * Check if a parser is registered
   * @param {string} id - Parser identifier
   * @returns {boolean} True if registered
   */
  has(id) {
    return this.parsers.has(id);
  }
}

// Create global registry instance
export const parserRegistry = new ParserRegistry();

/**
 * Example: Plain text parser (default fallback)
 */
export class PlainTextParser extends Parser {
  static get metadata() {
    return {
      name: 'Plain Text',
      version: '1.0.0',
      languages: ['text', 'plain', 'plaintext'],
      fileExtensions: ['txt'],
      mimeTypes: ['text/plain']
    };
  }

  parse(text) {
    return Parser.escapeHtml(text);
  }
}

// Register default parser
parserRegistry.register('plaintext', PlainTextParser);

/**
 * Example: Simple JavaScript parser
 */
export class JavaScriptParser extends Parser {
  static get metadata() {
    return {
      name: 'JavaScript',
      version: '1.0.0',
      languages: ['javascript', 'js', 'jsx'],
      fileExtensions: ['js', 'jsx', 'mjs', 'cjs'],
      mimeTypes: ['text/javascript', 'application/javascript']
    };
  }

  parse(text) {
    // Process the text character by character to handle syntax highlighting
    // while preserving exact positions
    
    // First, identify all tokens with their positions
    const tokenize = (text) => {
      const tokens = [];
      
      // Comments
      const commentRegex = /(\/\/.*$|\/\*[\s\S]*?\*\/)/gm;
      let match;
      while ((match = commentRegex.exec(text)) !== null) {
        tokens.push({ start: match.index, end: match.index + match[0].length, type: 'comment', text: match[0] });
      }
      
      // Strings (all types)
      const stringRegex = /(["'`])(?:(?=(\\?))\2[\s\S])*?\1/g;
      while ((match = stringRegex.exec(text)) !== null) {
        tokens.push({ start: match.index, end: match.index + match[0].length, type: 'string', text: match[0] });
      }
      
      // Keywords
      const keywordRegex = /\b(const|let|var|function|return|if|else|for|while|do|switch|case|break|continue|class|extends|import|export|default|async|await|try|catch|finally|throw|new|this|super|typeof|instanceof|in|of|void|delete|yield)\b/g;
      while ((match = keywordRegex.exec(text)) !== null) {
        tokens.push({ start: match.index, end: match.index + match[0].length, type: 'keyword', text: match[0] });
      }
      
      // Numbers
      const numberRegex = /\b(\d+\.?\d*)\b/g;
      while ((match = numberRegex.exec(text)) !== null) {
        tokens.push({ start: match.index, end: match.index + match[0].length, type: 'number', text: match[0] });
      }
      
      // Functions
      const functionRegex = /\b([a-zA-Z_$][a-zA-Z0-9_$]*)\s*(?=\()/g;
      while ((match = functionRegex.exec(text)) !== null) {
        const funcName = match[1];
        tokens.push({ start: match.index, end: match.index + funcName.length, type: 'function', text: funcName });
      }
      
      // Sort tokens by position and remove overlaps (earlier tokens take precedence)
      tokens.sort((a, b) => a.start - b.start);
      const finalTokens = [];
      let lastEnd = -1;
      
      for (const token of tokens) {
        if (token.start >= lastEnd) {
          finalTokens.push(token);
          lastEnd = token.end;
        }
      }
      
      return finalTokens;
    };
    
    const tokens = tokenize(text);
    let result = '';
    let lastIndex = 0;
    
    for (const token of tokens) {
      // Add any text before this token
      if (token.start > lastIndex) {
        result += Parser.escapeHtml(text.slice(lastIndex, token.start));
      }
      
      // Add the token with highlighting
      const escapedText = Parser.escapeHtml(token.text);
      result += Parser.span(escapedText, `syn-${token.type}`);
      
      lastIndex = token.end;
    }
    
    // Add any remaining text
    if (lastIndex < text.length) {
      result += Parser.escapeHtml(text.slice(lastIndex));
    }
    
    return result;
  }
}

// Register JavaScript parser
parserRegistry.register('javascript', JavaScriptParser);